import { API_URLS, formatCurrency, formatCurrencyCompact } from "@solomon-apps/shared";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

/**
 * Raw distribution data from API
 */
export interface Distribution {
    id: string;
    yieldAmount: string;
    transactionSignature: string;
    transactionTimestamp: string | number;
    stakingBalance: string;
    sevenDayApy: string | number;
    allTimeApy: string | number;
}

/**
 * Processed distribution for display
 */
export interface ProcessedDistribution {
    id: string;
    dateStr: string;
    dateShortStr: string;
    sigShort: string;
    sigFull: string;
    /** Seven day APY as a percent string. Source: `sevenDayApy`. */
    apyStr: string;
    /** Seven day APY as a percent number. Source: `sevenDayApy`. */
    apyNum: number;
    /** All time APY as a percent string. Source: `allTimeApy`. */
    allTimeApyStr: string;
    /** All time APY as a percent number. Source: `allTimeApy`. */
    allTimeApyNum: number;
    stakedNum: number;
    stakedStr: string;
    stakedStrCompact: string;
    yieldNum: number;
    amountStr: string;
    amountStrCompact: string;
    timestamp: number;
}

/**
 * Yield distributions data with processed items
 */
export interface YieldDistributionsData {
    items: ProcessedDistribution[];
    total?: number;
}

type YieldDistributionsApiResponse = Distribution[] | { data?: Distribution[]; items?: Distribution[]; total?: number };

/**
 * Convert raw token amount to human-readable number
 */
function toUsdNumber(raw: string | number | null | undefined, decimals: number): number {
    if (raw === null || raw === undefined) return 0;
    const s = String(raw);
    const num = Number(s);
    if (!Number.isFinite(num)) return 0;
    return num / Math.pow(10, decimals);
}

/**
 * Truncate a transaction signature for display
 */
function truncate(s: string): string {
    return s?.length > 12 ? `${s.slice(0, 6)}...${s.slice(-6)}` : s;
}

/**
 * Hook to fetch historical yield distributions
 *
 * @param usdvDecimals - Number of decimals for USDv token (required, query only runs when finite)
 * @param limit - Maximum items to return (default 100, clamped 1-100)
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useYieldDistributions(6);
 * if (data) {
 *   data.items.forEach(d => {
 *     console.log(`${d.dateStr}: ${d.amountStr} (${d.apyStr})`);
 *   });
 * }
 * ```
 */
export function useYieldDistributions(usdvDecimals: number, limit = 100): UseQueryResult<YieldDistributionsData> {
    return useQuery<YieldDistributionsData>({
        queryKey: ["yield-distributions", usdvDecimals, limit],
        enabled: Number.isFinite(usdvDecimals),
        queryFn: async ({ signal }) => {
            const safeLimit = Math.min(Math.max(limit, 1), 100);
            const url = `${API_URLS.SOLOMON_DATA}/api/solomon-protocol/staking/yield-distributions?limit=${safeLimit}`;
            const response = await fetch(url, { signal });
            if (!response.ok) throw new Error("Failed to fetch distributions");
            const json: YieldDistributionsApiResponse = await response.json();

            const rawItems: Distribution[] = Array.isArray(json)
                ? json
                : Array.isArray(json?.data)
                  ? json.data
                  : Array.isArray(json?.items)
                    ? json.items
                    : [];

            const dateFormatter = new Intl.DateTimeFormat("en-US", {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false
            });
            const shortDateFormatter = new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "2-digit"
            });

            const items: ProcessedDistribution[] = rawItems
                .map((d) => {
                    let timestamp =
                        typeof d.transactionTimestamp === "number"
                            ? d.transactionTimestamp
                            : new Date(d.transactionTimestamp).getTime();

                    // Normalize seconds to milliseconds
                    if (timestamp < 1e12) {
                        timestamp = timestamp * 1000;
                    }

                    const apyNum = Number(d.sevenDayApy ?? 0) * 100;
                    const allTimeApyNum = Number(d.allTimeApy ?? 0) * 100;
                    const stakedNum = toUsdNumber(d.stakingBalance, usdvDecimals);
                    const yieldNum = toUsdNumber(d.yieldAmount, usdvDecimals);

                    return {
                        id: d.id,
                        dateStr: dateFormatter.format(new Date(timestamp)),
                        dateShortStr: shortDateFormatter.format(new Date(timestamp)),
                        sigShort: truncate(d.transactionSignature),
                        sigFull: d.transactionSignature,
                        apyStr: `${apyNum.toFixed(2)}%`,
                        apyNum,
                        allTimeApyStr: `${allTimeApyNum.toFixed(2)}%`,
                        allTimeApyNum,
                        stakedNum,
                        stakedStr: formatCurrency(stakedNum),
                        stakedStrCompact: formatCurrencyCompact(stakedNum),
                        yieldNum,
                        amountStr: formatCurrency(yieldNum),
                        amountStrCompact: formatCurrencyCompact(yieldNum),
                        timestamp
                    };
                })
                .sort((a, b) => b.timestamp - a.timestamp);

            const total: number | undefined =
                !Array.isArray(json) && typeof json.total === "number" ? json.total : undefined;

            return { items, total };
        },
        staleTime: 24 * 60 * 60 * 1000, // 24 hours
        gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: 4 * 60 * 60 * 1000, // 4 hours
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
    });
}
